// Phase 4 - US2: Survey Types (T075)

import { Country, State, City } from './profile.types';
import { SurveyVisibilityLevel, SurveyVisibilitySummary } from './survey-visibility.types';

export enum GeographicScope {
  NATIONAL = 'NATIONAL',
  STATE = 'STATE',
  CITY = 'CITY',
}

export enum SurveyStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
}

export enum SurveyAnalysisStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED_UNPUBLISHED = 'COMPLETED_UNPUBLISHED',
  PUBLISHED = 'PUBLISHED',
}

export enum ComplementaryAnalysisStatus {
  DRAFT = 'DRAFT',
  PENDING_SCAN = 'PENDING_SCAN',
  READY_TO_PUBLISH = 'READY_TO_PUBLISH',
  PUBLISHED = 'PUBLISHED',
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  CHECKBOX = 'CHECKBOX',
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  RATING = 'RATING',
}

export interface Survey {
  id: string;
  publicAuditHash?: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  geographicScope: GeographicScope;
  countryId?: string;
  stateId?: string;
  cityId?: string;
  contractorId: string;
  surveyValue: number;
  status: SurveyStatus;
  visibilityLevel: SurveyVisibilityLevel;
  disclosureDate?: string | null;
  visibility?: SurveyVisibilitySummary;
  contractor?: {
    id: string;
    tradeName: string;
  };
  country?: Country;
  state?: State;
  city?: City;
  questions?: Question[];
  _count?: {
    responses: number;
  };
  analysis?: {
    status: SurveyAnalysisStatus;
    analyzedResponseCount: number | null;
    generatedAt: string | null;
    versionFreshness?: 'CURRENT' | 'OUTDATED' | 'LEGACY';
  } | null;
  complementaryAnalysis?: {
    status: ComplementaryAnalysisStatus;
  } | null;
  // Feature 034: presente apenas na listagem do administrador.
  previewAnalysisCount?: number;
  latestPreviewAnalysisAt?: string | null;
  tags?: Array<{
    id: string;
    name: string;
    status: 'ACTIVE' | 'INACTIVE';
  }>;
  createdAt: string;
  updatedAt: string;
  hasUserResponded?: boolean; // Added for participant view
  participantCount?: number; // Added for completed surveys analytics
  allowGuestParticipation?: boolean;
  isElectoral?: boolean;
}

export interface UpdateSurveyTagsData {
  tagIds: string[];
}

export interface Question {
  id: string;
  surveyId: string;
  order: number;
  shortDescription: string;
  longDescription?: string;
  questionType: QuestionType;
  options?: QuestionOptions;
  effectiveChoices?: EffectiveChoiceItem[];
  required: boolean;
  allowsTypedFilter?: boolean;
  requiresFilterToShowOptions?: boolean;
  filterLabel?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EffectiveChoiceItem {
  value: string;
  label: string;
  filter?: string;
  kind: 'base' | 'null' | 'dont_know';
}

export interface QuestionOptions {
  choices?: Array<{ value: string; label: string; filter?: string }>;
  min?: number;
  max?: number;
  maxLength?: number;
  randomizeChoices?: boolean;
  includeNullOption?: boolean;
  includeDontKnowOption?: boolean;
  maxSelections?: number;
  enforceSelectionLimit?: boolean;
}

export interface CreateSurveyData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  geographicScope: GeographicScope;
  countryId?: string;
  stateId?: string;
  cityId?: string;
  contractorId?: string;
  surveyValue: number;
  allowGuestParticipation?: boolean;
  visibilityLevel?: SurveyVisibilityLevel;
  disclosureDate?: string | null;
  isElectoral?: boolean;
}

export interface UpdateSurveyData extends Partial<CreateSurveyData> {
  status?: SurveyStatus;
}

export interface SurveyVisibilityResponse {
  id: string;
  visibilityLevel: SurveyVisibilityLevel;
  disclosureDate: string | null;
}

export interface CreateQuestionData {
  order: number;
  shortDescription: string;
  longDescription?: string;
  questionType: QuestionType;
  options?: QuestionOptions;
  required?: boolean;
  allowsTypedFilter?: boolean;
  requiresFilterToShowOptions?: boolean;
  filterLabel?: string | null;
  questionGroupId?: string | null;
}

export interface UpdateQuestionData extends Partial<CreateQuestionData> {}

export interface SurveyTakeResponse {
  id: string;
  name: string;
  description: string;
  questions: Question[];
}
